import * as THREE from "three"
import {GLTFLoader} from "../vendor/three/examples/jsm/loaders/GLTFLoader.js"
import {CONTAIN_DEFAULT, CONTAIN_MAX, CONTAIN_MIN} from "./ar_zones.js"
import {createSeekChevron, disposeSeekChevron, tickSeekChevron} from "./ar_seek_chevron.js"

export {THREE, GLTFLoader}

export const AR_FOV_H = 65 * (Math.PI / 180)

// DeviceOrientation → back-camera look, same convention as Three.DeviceOrientationControls:
// Euler YXZ (beta, alpha, −gamma), then −90° about X so −Z is the rear camera, not the
// screen or the top of the phone. Heading 0 looks north in our ENU (x east, y up, z south).
const _orientEuler = new THREE.Euler()
const _orientQ = new THREE.Quaternion()
const _orientQ1 = new THREE.Quaternion(-Math.sqrt(0.5), 0, 0, Math.sqrt(0.5))
const _orientQ0 = new THREE.Quaternion()
const _orientZee = new THREE.Vector3(0, 0, 1)
const _orientLook = new THREE.Vector3()

export function headingPitchFromDevice(alphaDeg, betaDeg, gammaDeg, screenDeg) {
  const alpha = ((Number(alphaDeg) || 0) * Math.PI) / 180
  const beta = ((Number(betaDeg) || 0) * Math.PI) / 180
  const gamma = ((Number(gammaDeg) || 0) * Math.PI) / 180
  const orient = ((Number(screenDeg) || 0) * Math.PI) / 180

  _orientEuler.set(beta, alpha, -gamma, "YXZ")
  _orientQ.setFromEuler(_orientEuler)
  _orientQ.multiply(_orientQ1)
  _orientQ.multiply(_orientQ0.setFromAxisAngle(_orientZee, -orient))
  _orientLook.set(0, 0, -1).applyQuaternion(_orientQ)

  const east = _orientLook.x
  const up = _orientLook.y
  const north = -_orientLook.z
  const horiz = Math.hypot(east, north)
  let heading = (Math.atan2(east, north) * 180) / Math.PI
  if (heading < 0) heading += 360
  const pitch = (Math.atan2(up, Math.max(horiz, 1e-6)) * 180) / Math.PI
  return {heading, pitch}
}

export function hasWebGL() {
  try {
    const canvas = document.createElement("canvas")
    return Boolean(canvas.getContext("webgl2") || canvas.getContext("webgl"))
  } catch {
    return false
  }
}

function boxSize(object) {
  object.updateWorldMatrix(true, true)
  const box = new THREE.Box3().setFromObject(object)
  const size = new THREE.Vector3()
  if (!box.isEmpty()) box.getSize(size)
  return size
}

function equivalentDiameter(object) {
  const size = boxSize(object)
  const diameter = Math.hypot(size.x, size.y, size.z)
  return diameter > 1e-8 ? diameter : 1
}

function longestEdge(object) {
  const size = boxSize(object)
  return Math.max(size.x, size.y, size.z, 1e-8)
}

function disposeObject(root) {
  if (!root) return
  root.traverse((node) => {
    if (node.geometry) node.geometry.dispose()
    const material = node.material
    if (!material) return
    const materials = Array.isArray(material) ? material : [material]
    for (const item of materials) {
      for (const key of Object.keys(item)) {
        const value = item[key]
        if (value && value.isTexture) value.dispose()
      }
      item.dispose()
    }
  })
}

export function createModelLayer(canvas) {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    alpha: true,
    antialias: false,
    powerPreference: "high-performance",
    // QA Mark shots draw this canvas into a JPEG. Without this the buffer is
    // empty after present.
    preserveDrawingBuffer: true,
  })
  renderer.setClearColor(0x000000, 0)
  renderer.outputColorSpace = THREE.SRGBColorSpace

  const scene = new THREE.Scene()
  scene.add(new THREE.HemisphereLight(0xffffff, 0x334155, 1.15))
  const sun = new THREE.DirectionalLight(0xfff7e6, 0.85)
  sun.position.set(0.4, 1, 0.25)
  scene.add(sun)

  const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 2000)
  camera.rotation.order = "YXZ"

  const seekChevron = createSeekChevron()
  camera.add(seekChevron)
  let seekWanted = false
  let seekEnu = null
  let seekSizeM = 1.5
  let seekAspect = 1
  let headingDeg = 0
  let pitchDeg = 0
  let viewW = 1
  let viewH = 1

  const loader = new GLTFLoader()
  const textureLoader = new THREE.TextureLoader()
  const cache = new Map()
  const videoCache = new Map()
  const imageCache = new Map()
  const clock = new THREE.Clock()

  let currentUrl = null
  let currentKind = null
  let root = null
  let mixer = null
  let videoEl = null
  let videoEnded = false
  let loadToken = 0
  let loadingUrl = null
  let fade = null
  let containFill = CONTAIN_DEFAULT

  function setContainFill(value) {
    const n = Number(value)
    containFill = Number.isFinite(n)
      ? Math.min(CONTAIN_MAX, Math.max(CONTAIN_MIN, n))
      : CONTAIN_DEFAULT
  }

  function resize(width, height, dpr) {
    const w = Math.max(1, width)
    const h = Math.max(1, height)
    viewW = w
    viewH = h
    const pixelRatio = Math.min(dpr || 1, 2)
    renderer.setPixelRatio(pixelRatio)
    renderer.setSize(w, h, false)
    camera.aspect = w / h
    const fovV = 2 * Math.atan(Math.tan(AR_FOV_H / 2) / camera.aspect)
    camera.fov = (fovV * 180) / Math.PI
    camera.updateProjectionMatrix()
  }

  function setCamera(heading, pitch) {
    headingDeg = Number(heading) || 0
    pitchDeg = Number(pitch) || 0
    const headingRad = (headingDeg * Math.PI) / 180
    const pitchRad = (pitchDeg * Math.PI) / 180
    camera.position.set(0, 0, 0)
    camera.rotation.set(-pitchRad, -headingRad, 0)
  }

  function clearSeek() {
    seekWanted = false
    seekEnu = null
  }

  function rewindVideo(el) {
    if (!el) return
    el.pause()
    try {
      el.currentTime = 0
    } catch {
      /* ignore */
    }
  }

  function hideModel() {
    if (mixer) {
      mixer.stopAllAction()
      mixer = null
    }
    if (videoEl) {
      rewindVideo(videoEl)
      videoEl = null
    }
    videoEnded = false
    if (root) {
      scene.remove(root)
      if (currentKind === "mp4" || currentKind === "texture") {
        root.traverse((node) => {
          if (node.geometry) node.geometry.dispose()
          const material = node.material
          if (material && !material.map?.isVideoTexture) material.dispose()
        })
      }
      root = null
    }
  }

  function playIdle(gltf, object) {
    const clips = gltf.animations || []
    if (!clips.length) return
    const idle =
      clips.find((clip) => String(clip.name).toLowerCase() === "idle") || clips[0]
    mixer = new THREE.AnimationMixer(object)
    mixer.clipAction(idle).play()
  }

  function isMp4(url, kind) {
    if (kind === "mp4") return true
    return typeof url === "string" && url.toLowerCase().includes(".mp4")
  }

  function isTexture(url, kind) {
    if (kind === "texture") return true
    if (typeof url !== "string") return false
    const lower = url.toLowerCase()
    return [".jpg", ".jpeg", ".png", ".webp"].some((ext) => lower.includes(ext))
  }

  function overlayKind(url, kind) {
    if (isMp4(url, kind)) return "mp4"
    if (isTexture(url, kind)) return "texture"
    return "glb"
  }

  function makeVideoEl(url) {
    const el = document.createElement("video")
    el.muted = true
    el.defaultMuted = true
    el.playsInline = true
    el.setAttribute("muted", "")
    el.setAttribute("playsinline", "")
    el.setAttribute("webkit-playsinline", "")
    el.loop = false
    el.preload = "auto"
    el.crossOrigin = "anonymous"
    el.src = url
    return el
  }

  function videoAspect(el) {
    const w = el.videoWidth || 0
    const h = el.videoHeight || 0
    if (w > 0 && h > 0) return w / h
    return 1
  }

  function posterIsLit(ctx, w, h) {
    try {
      const step = Math.max(1, Math.floor(Math.min(w, h) / 6))
      const {data} = ctx.getImageData(0, 0, w, h)
      let lit = 0
      for (let y = 0; y < h; y += step) {
        for (let x = 0; x < w; x += step) {
          const i = (y * w + x) * 4
          if (data[i] + data[i + 1] + data[i + 2] > 30) lit += 1
        }
      }
      return lit > 2
    } catch {
      return true
    }
  }

  function grabPoster(entry) {
    const el = entry.el
    const w = el.videoWidth
    const h = el.videoHeight
    if (!w || !h || el.readyState < 2) return false

    const canvas = document.createElement("canvas")
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext("2d")
    if (!ctx) return false
    try {
      ctx.drawImage(el, 0, 0, w, h)
    } catch {
      return false
    }
    if (!posterIsLit(ctx, w, h)) return false

    if (entry.poster) entry.poster.dispose()
    entry.poster = new THREE.CanvasTexture(canvas)
    entry.poster.colorSpace = THREE.SRGBColorSpace
    entry.aspect = w / h
    if (!entry.texture) {
      entry.texture = new THREE.VideoTexture(el)
      entry.texture.colorSpace = THREE.SRGBColorSpace
    }
    entry.ready = true
    return true
  }

  function primePoster(entry) {
    const el = entry.el
    if (entry.priming || entry.ready) return
    entry.priming = true

    const done = () => {
      grabPoster(entry)
      el.pause()
      try {
        el.currentTime = 0
      } catch {
        /* ignore */
      }
      entry.priming = false
    }

    const play = el.play()
    if (typeof el.requestVideoFrameCallback === "function") {
      el.requestVideoFrameCallback(() => done())
      return
    }
    if (play && typeof play.then === "function") {
      play.then(() => {
        if (!grabPoster(entry)) {
          el.currentTime = Math.min(0.08, (el.duration || 1) * 0.01)
        } else {
          done()
        }
      }).catch(() => {
        entry.priming = false
      })
      return
    }
    entry.priming = false
  }

  function stillMap(entry) {
    return entry.poster || entry.texture
  }

  function setVideoMap(playing) {
    if (!root) return
    const entry = currentUrl ? videoCache.get(currentUrl) : null
    if (!entry) return
    const map = playing ? entry.texture : stillMap(entry)
    if (!map) return
    root.traverse((node) => {
      if (node.material && node.material.map !== map) {
        node.material.map = map
        node.material.needsUpdate = true
      }
    })
    if (map.needsUpdate !== undefined) map.needsUpdate = true
  }

  function ensureVideoEntry(url) {
    const hit = videoCache.get(url)
    if (hit) return hit

    const el = makeVideoEl(url)
    const entry = {el, texture: null, poster: null, aspect: 1, ready: false}
    videoCache.set(url, entry)

    const onReady = () => {
      if (grabPoster(entry)) return
      primePoster(entry)
    }

    el.addEventListener("loadeddata", onReady)
    el.addEventListener("loadedmetadata", onReady)
    el.addEventListener("seeked", () => {
      if (!entry.ready) grabPoster(entry)
    })
    el.addEventListener("ended", () => {
      if (videoEl === el) videoEnded = true
    })
    el.load()
    return entry
  }

  function attachPlane(map, aspect) {
    const ratio = aspect > 0 ? aspect : 1
    const width = ratio >= 1 ? 1 : ratio
    const height = ratio >= 1 ? 1 / ratio : 1
    const geom = new THREE.PlaneGeometry(width, height)
    const mat = new THREE.MeshBasicMaterial({
      map,
      toneMapped: false,
      side: THREE.DoubleSide,
      transparent: true,
    })
    const mesh = new THREE.Mesh(geom, mat)
    const group = new THREE.Group()
    group.add(mesh)
    // Longest edge is 1 in local units, so apparent size is that edge in metres.
    group.userData.equivDiameter = 1
    group.userData.longestEdge = 1
    group.userData.planeAspect = ratio
    return group
  }

  function attachVideoPlane(entry) {
    return attachPlane(stillMap(entry), entry.aspect)
  }

  function mountTexture(url, token) {
    const apply = (entry) => {
      if (token !== loadToken) return
      hideModel()
      root = attachPlane(entry.texture, entry.aspect)
      scene.add(root)
      loadingUrl = null
    }

    const hit = imageCache.get(url)
    if (hit) {
      apply(hit)
      return
    }

    textureLoader.load(
      url,
      (texture) => {
        texture.colorSpace = THREE.SRGBColorSpace
        const img = texture.image
        const w = img?.naturalWidth || img?.width || 1
        const h = img?.naturalHeight || img?.height || 1
        const entry = {texture, aspect: w / h}
        imageCache.set(url, entry)
        apply(entry)
      },
      undefined,
      (err) => {
        console.warn("Texture load failed", url, err)
        if (token === loadToken) {
          hideModel()
          loadingUrl = null
          currentUrl = null
          currentKind = null
        }
      }
    )
  }

  function mountVideo(url, token) {
    const entry = ensureVideoEntry(url)

    const show = () => {
      if (token !== loadToken) return
      if (!entry.ready || !stillMap(entry)) return
      hideModel()
      videoEl = entry.el
      videoEnded = false
      videoEl.pause()
      root = attachVideoPlane(entry)
      scene.add(root)
      loadingUrl = null
    }

    if (entry.ready && stillMap(entry)) {
      show()
      return
    }

    entry.el.addEventListener("loadeddata", () => {
      grabPoster(entry)
      show()
    })
  }

  function preloadVideos(urls) {
    for (const url of urls) {
      if (url) ensureVideoEntry(url)
    }
  }

  function warmVideos() {
    for (const entry of videoCache.values()) primePoster(entry)
  }

  function load(url, kind) {
    if (!url) {
      hideModel()
      currentUrl = null
      currentKind = null
      loadingUrl = null
      clearSeek()
      return
    }
    if (url === currentUrl && root) return
    if (url === loadingUrl) return

    hideModel()
    currentUrl = url
    currentKind = overlayKind(url, kind)
    loadingUrl = url
    const token = ++loadToken

    if (currentKind === "mp4") {
      mountVideo(url, token)
      return
    }

    if (currentKind === "texture") {
      mountTexture(url, token)
      return
    }

    const apply = (gltf) => {
      if (token !== loadToken) return
      hideModel()
      root = new THREE.Group()
      root.add(gltf.scene)
      scene.add(root)
      root.userData.equivDiameter = equivalentDiameter(gltf.scene)
      root.userData.longestEdge = longestEdge(gltf.scene)
      playIdle(gltf, gltf.scene)
      loadingUrl = null
    }

    const hit = cache.get(url)
    if (hit) {
      apply(hit)
      return
    }

    loader.load(
      url,
      (gltf) => {
        cache.set(url, gltf)
        apply(gltf)
      },
      undefined,
      (err) => {
        console.warn("GLB load failed", url, err)
        if (token === loadToken) {
          hideModel()
          loadingUrl = null
          currentUrl = null
          currentKind = null
        }
      }
    )
  }

  function playVideo() {
    if (!videoEl || videoEnded || videoEl.ended) return
    setVideoMap(true)
    const play = videoEl.play()
    if (play && typeof play.catch === "function") play.catch(() => {})
  }

  function pauseVideo() {
    if (!videoEl) return
    videoEl.pause()
    setVideoMap(true)
  }

  function videoTime() {
    if (!videoEl) return 0
    return Number(videoEl.currentTime) || 0
  }

  function preloadTextures(urls) {
    for (const url of urls) {
      if (!url || imageCache.has(url)) continue
      textureLoader.load(url, (texture) => {
        texture.colorSpace = THREE.SRGBColorSpace
        const img = texture.image
        const w = img?.naturalWidth || img?.width || 1
        const h = img?.naturalHeight || img?.height || 1
        imageCache.set(url, {texture, aspect: w / h})
      })
    }
  }

  function planeMeshFromTexture(entry) {
    const group = attachPlane(entry.texture, entry.aspect)
    const mesh = group.children[0]
    mesh.material.transparent = true
    return mesh
  }

  function setObjectOpacity(object, opacity) {
    object.traverse((node) => {
      const material = node.material
      if (!material) return
      const materials = Array.isArray(material) ? material : [material]
      for (const item of materials) {
        item.transparent = true
        item.opacity = opacity
        item.depthWrite = false
        item.needsUpdate = true
      }
    })
  }

  function crossfadeTo(url, durationMs, onDone) {
    if (!root || !url || url === currentUrl) {
      onDone?.()
      return
    }

    const begin = (entry) => {
      if (!root) {
        onDone?.()
        return
      }
      const toMesh = planeMeshFromTexture(entry)
      toMesh.material.opacity = 0
      toMesh.material.depthWrite = false
      toMesh.material.depthTest = false
      toMesh.renderOrder = 2
      const from = [...root.children]
      for (const node of from) {
        setObjectOpacity(node, 1)
        node.renderOrder = 1
        node.traverse((child) => {
          if (child.material) child.material.depthWrite = false
        })
      }
      root.add(toMesh)
      fade = {
        from,
        toMesh,
        startedAt: performance.now(),
        duration: Math.max(200, durationMs || 480),
        url,
        aspect: entry.aspect,
        onDone,
      }
    }

    const hit = imageCache.get(url)
    if (hit) {
      begin(hit)
      return
    }

    textureLoader.load(
      url,
      (texture) => {
        texture.colorSpace = THREE.SRGBColorSpace
        const img = texture.image
        const w = img?.naturalWidth || img?.width || 1
        const h = img?.naturalHeight || img?.height || 1
        const entry = {texture, aspect: w / h}
        imageCache.set(url, entry)
        begin(entry)
      },
      undefined,
      () => onDone?.()
    )
  }

  function tickFade(now) {
    if (!fade || !root) return
    const u = smoothstep((now - fade.startedAt) / fade.duration)
    for (const node of fade.from) setObjectOpacity(node, 1 - u)
    setObjectOpacity(fade.toMesh, u)
    if (u < 1) return

    for (const node of fade.from) {
      root.remove(node)
      if (node.geometry) node.geometry.dispose()
    }
    if (videoEl) {
      videoEl.pause()
      videoEl = null
    }
    videoEnded = false
    currentUrl = fade.url
    currentKind = "texture"
    root.userData.planeAspect = fade.aspect
    root.userData.equivDiameter = 1
    root.userData.longestEdge = 1
    const done = fade.onDone
    fade = null
    done?.()
  }

  function resetVideo() {
    if (!videoEl) return
    videoEl.pause()
    videoEnded = false
    try {
      videoEl.currentTime = 0
    } catch {
      /* ignore */
    }
    const entry = currentUrl ? videoCache.get(currentUrl) : null
    if (entry) grabPoster(entry)
    setVideoMap(false)
  }

  function videoState() {
    if (!videoEl) return {kind: currentKind, playing: false, ended: false, error: false, duration: 0}
    return {
      kind: "mp4",
      playing: !videoEl.paused && !videoEl.ended,
      ended: videoEnded || videoEl.ended,
      // A failed load never fires `ended`; the handoff must not wait for it.
      error: videoEl.error != null,
      duration: Number.isFinite(videoEl.duration) ? videoEl.duration : 0,
    }
  }

  /**
   * Whether there is a decoded frame to show for the mounted asset.
   *
   * `videoState()` reports `kind: "mp4"` from mount, and a paused clip renders
   * `stillMap`, which needs `entry.poster` — `grabPoster` declines while the
   * element has no dimensions or the frame is unlit. So `vid: "pause"` in a QA
   * trace does not prove anything was on screen. Field walks held the pin in
   * frame for tens of seconds and never played; this is the field that says
   * whether they had a poster to look at.
   */
  function overlayReady() {
    if (!currentUrl) return false
    if (currentKind === "mp4") {
      const entry = videoCache.get(currentUrl)
      return Boolean(entry?.ready && stillMap(entry))
    }
    return Boolean(root && root.userData.planeAspect)
  }
  function authoredSize(mode) {
    if (mode === "longest") return root.userData.longestEdge || root.userData.equivDiameter || 1
    return root.userData.equivDiameter || 1
  }

  function pose(enu, facing, yawDeg, sizeM, sizeMode) {
    seekEnu = enu
    seekSizeM = Number(sizeM) > 0 ? Number(sizeM) : 1.5
    seekAspect = root?.userData.planeAspect || 1
    seekWanted = true
    if (!root) return
    const east = Number(enu.east) || 0
    const north = Number(enu.north) || 0
    const up = Number(enu.up) || 0
    root.position.set(east, up, -north)
    const meters = Number(sizeM) > 0 ? Number(sizeM) : 1.5
    const mode = sizeMode || "diameter"
    root.userData.sizeMode = mode
    root.scale.setScalar(meters / authoredSize(mode))

    const horizSq = east * east + north * north
    root.up.set(0, 1, 0)
    root.quaternion.identity()
    if (facing === "world") {
      const yaw = ((Number(yawDeg) || 0) * Math.PI) / 180
      root.lookAt(east + Math.sin(yaw), up, -north - Math.cos(yaw))
    } else if (horizSq > 0.25) {
      root.lookAt(0, up, 0)
    }
  }

  function snapshotPose() {
    if (!root) return null
    return {
      position: root.position.clone(),
      quaternion: root.quaternion.clone(),
      scale: root.scale.x,
      sizeMode: root.userData.sizeMode || "diameter",
    }
  }

  function smoothstep(t) {
    const x = t <= 0 ? 0 : t >= 1 ? 1 : t
    return x * x * (3 - 2 * x)
  }

  function shortestQuat(from, to) {
    const end = to.clone()
    if (from.dot(end) < 0) {
      end.x = -end.x
      end.y = -end.y
      end.z = -end.z
      end.w = -end.w
    }
    return end
  }

  function framedPose(sizeM, planeAspect, mode) {
    const aspect = planeAspect > 0 ? planeAspect : 1
    const width = aspect >= 1 ? sizeM : sizeM * aspect
    const height = aspect >= 1 ? sizeM / aspect : sizeM
    const fovH = AR_FOV_H
    const fovV = 2 * Math.atan(Math.tan(fovH / 2) / Math.max(camera.aspect, 1e-6))
    const distW = width / 2 / Math.tan(fovH / 2)
    const distH = height / 2 / Math.tan(fovV / 2)
    const fill = containFill
    let dist
    if (mode === "cover") {
      dist = Math.min(distW, distH)
    } else {
      dist = Math.max(distW / fill, distH / fill)
    }
    dist = Math.max(0.45, dist)
    const position = new THREE.Vector3(0, 0, -1)
      .applyQuaternion(camera.quaternion)
      .multiplyScalar(dist)
    const sizeMode = root.userData.sizeMode || "diameter"
    return {
      position,
      quaternion: camera.quaternion.clone(),
      scale: sizeM / authoredSize(sizeMode),
      sizeMode,
    }
  }

  function applyApproach(snapshot, t, mode) {
    clearSeek()
    if (!root || !snapshot) return
    const sizeMode = snapshot.sizeMode || root.userData.sizeMode || "diameter"
    const authored = authoredSize(sizeMode)
    const sizeM = snapshot.scale * authored
    const end = framedPose(sizeM, root.userData.planeAspect || 1, mode || "contain")
    const u = smoothstep(t)
    root.position.lerpVectors(snapshot.position, end.position, u)
    root.quaternion.copy(snapshot.quaternion).slerp(shortestQuat(snapshot.quaternion, end.quaternion), u)
    root.scale.setScalar(snapshot.scale)
  }

  function render() {
    const dt = clock.getDelta()
    if (mixer) mixer.update(dt)
    if (videoEl && !videoEl.paused && currentUrl) {
      const entry = videoCache.get(currentUrl)
      if (entry?.texture) entry.texture.needsUpdate = true
    }
    tickFade(performance.now())
    camera.updateMatrixWorld()
    tickSeekChevron({
      chevron: seekChevron,
      heading: headingDeg,
      pitch: pitchDeg,
      w: viewW,
      h: viewH,
      enu: seekEnu,
      sizeM: seekSizeM,
      planeAspect: seekAspect,
      dt,
      show: seekWanted,
    })
    renderer.render(scene, camera)
  }

  function dispose() {
    loadToken += 1
    hideModel()
    clearSeek()
    camera.remove(seekChevron)
    disposeSeekChevron(seekChevron)
    currentUrl = null
    currentKind = null
    loadingUrl = null
    for (const gltf of cache.values()) disposeObject(gltf.scene)
    cache.clear()
    for (const entry of videoCache.values()) {
      rewindVideo(entry.el)
      entry.el.removeAttribute("src")
      entry.el.load()
      entry.texture?.dispose()
      entry.poster?.dispose()
    }
    videoCache.clear()
    for (const entry of imageCache.values()) {
      entry.texture?.dispose()
    }
    imageCache.clear()
    renderer.dispose()
    const gl = renderer.getContext()
    gl?.getExtension("WEBGL_lose_context")?.loseContext()
  }

  function activeUrl() {
    return root ? currentUrl : null
  }

  const raycaster = new THREE.Raycaster()
  const ndc = new THREE.Vector2()

  function hitTest(ndcX, ndcY) {
    if (!root) return false
    ndc.set(ndcX, ndcY)
    raycaster.setFromCamera(ndc, camera)
    return raycaster.intersectObject(root, true).length > 0
  }

  return {
    resize,
    setCamera,
    setContainFill,
    load,
    pose,
    snapshotPose,
    applyApproach,
    render,
    dispose,
    activeUrl,
    hitTest,
    playVideo,
    pauseVideo,
    videoTime,
    resetVideo,
    videoState,
    overlayReady,
    planeAspect: () => (root ? root.userData.planeAspect || 1 : 1),
    preloadVideos,
    preloadTextures,
    crossfadeTo,
    warmVideos,
  }
}
